import React, { useState } from "react";
import { TabContent, TabPane, Nav, NavItem, NavLink, Button } from "reactstrap";
import { FA } from "components/FA";
import { modalService } from "services";
import styles from "./Help.module.css";

const Tab = {
  BASIC: "basic",
  ADVANCED: "advanced",
};

const Help = ({ isModal = false, close }) => {
  const [tab, setTab] = useState(Tab.BASIC);

  const handleToggle = (t) => {
    if (tab !== t) setTab(t);
  };
  return (
    <>
      <h3 className="card-header">
        How to Query{" "}
        {isModal && (
          <FA title="Close" onClick={() => close()} icon="times" size="xs" />
        )}
      </h3>
      <div className="card-body">
        <Nav tabs className={styles.nav}>
          <NavItem>
            <NavLink
              className={tab === Tab.BASIC ? "active" : ""}
              onClick={() => {
                handleToggle(Tab.BASIC);
              }}
            >
              Basic Usage
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={tab === Tab.ADVANCED ? "active" : ""}
              onClick={() => {
                handleToggle(Tab.ADVANCED);
              }}
            >
              Advanced Usage
            </NavLink>
          </NavItem>
        </Nav>
        <TabContent activeTab={tab} className={styles.content}>
          {tab === Tab.BASIC && (
            <TabPane className="active">
              <p className="card-text">
                The basic and default usage is to use one query on several
                fields. It is possible to specify filters on specific fields.
              </p>
              <h5 className="card-subtitle">Simple query string syntax</h5>
              <p className="card-text">
                The <code>simple_query_string</code> query supports the
                following operators:
              </p>
              <ul>
                <li>
                  <code>+</code> signifies AND operation
                </li>
                <li>
                  <code>|</code> signifies OR operation
                </li>
                <li>
                  <code>-</code> negates a single token
                </li>
                <li>
                  <code>"</code> wraps a number of tokens to signify a phrase
                  for searching
                </li>
                <li>
                  <code>*</code> at the end of a term signifies a prefix query
                </li>
                <li>
                  <code>(</code> and <code>)</code> signify precedence
                </li>
                <li>
                  <code>~N</code> after a word signifies edit distance
                  (fuzziness)
                </li>
                <li>
                  <code>~N</code> after a phrase signifies slop amount
                  (proximity search)
                </li>
              </ul>
              <p className="card-text">
                To use one of these characters literally, escape it with a
                preceding backslash <code>\</code>.
              </p>
              <h5 className="card-subtitle">Example:</h5>
              <p className="card-text">
                <code>"fried eggs" +(eggplant | potato) -frittata</code>
              </p>
            </TabPane>
          )}
          {tab === Tab.ADVANCED && (
            <TabPane className="active">
              <p className="card-text">
                Using the advanced text query enables combining fields in which
                to search specific text. It is possible to specify filters on
                specific fields.
              </p>
              <h5 className="card-subtitle">Query string syntax</h5>
              <p className="card-text">
                The <code>query_string</code> query supports the following
                operators:
              </p>
              <ul>
                <li>
                  <code>AND</code> or <code>&amp;&amp;</code> signifies AND
                  operation
                </li>
                <li>
                  <code>AND</code> or <code>||</code> signifies OR operation
                </li>
                <li>
                  <code>NOT</code> or <code>!</code> signifies NOT operation
                </li>
                <li>
                  <code>+</code> signifies token must be present
                </li>
                <li>
                  <code>-</code> negates a single token
                </li>
                <li>
                  <code>"</code> wraps a number of tokens to signify a phrase
                  for searching
                </li>
                <li>
                  <code>?</code> wildcard to replace a single character
                </li>
                <li>
                  <code>*</code> wildcard to replace zero or more characters
                </li>
                <li>
                  <code>~N</code> after a word signifies edit distance
                  (fuzziness)
                </li>
                <li>
                  <code>~N</code> after a phrase signifies slop amount
                  (proximity search)
                </li>
                <li>
                  <code>[min TO max]</code> specifies inclusive ranges for
                  dates, numeric or string fields
                </li>
                <li>
                  <code>
                    {"{"}min TO max{"}"}
                  </code>{" "}
                  specifies exclusive ranges for dates, numeric or string fields
                </li>
                <li>
                  ranges with one side unbounded can use the following operators{" "}
                  <code>&gt;</code>, <code>&gt;=</code>, <code>&lt;</code> or{" "}
                  <code>&lt;=</code>
                </li>
                <li>
                  <code>^N</code> after a token signifies importance (boosting).
                  Default is <code>1</code>
                </li>
                <li>
                  Multiple terms or clauses can be grouped together with
                  parentheses <code>(</code> and <code>)</code>
                </li>
              </ul>
              <h5 className="card-subtitle">Wildcards</h5>
              <p className="card-text">
                Be aware that wildcard queries can use an enormous amount of
                memory and perform very badly.
              </p>
              <p className="card-text">
                Allowing a wildcard at the beginning of a word is particularly
                heavy, because all terms in the index need to be examined, just
                in case they match.
              </p>
              <h5 className="card-subtitle">
                Avoid mixing fuzziness with wildcards
              </h5>
              <p className="card-text">
                Mixing fuzzy and wildcard operators is not supported. When
                mixed, one of the operators is not applied
              </p>
              <h5 className="card-subtitle">Reserved characters</h5>
              <p className="card-text">
                You should escape the reserved characters them with a leading
                backslash:{" "}
                <code>{'+ - = && || > < ! ( ) { } [ ] ^ " ~ * ? : \\ /'}</code>
              </p>
              <p className="card-text">
                <code>&lt;</code> and <code>&gt;</code> can’t be escaped at all.
                The only way to prevent them from attempting to create a range
                query is to remove them from the query string entirely.
              </p>
            </TabPane>
          )}
        </TabContent>
      </div>
    </>
  );
};
const openHelpModal = () =>
  modalService.open({
    id: "help",
    children: <Help isModal={true} close={() => modalService.close("help")} />,
  });

const HelpButton = ({ size, color, className }) => (
  <Button
    onClick={openHelpModal}
    size={size}
    color={color}
    className={className}
  >
    <FA icon="info" size="xs" /> Help
  </Button>
);

export { Help, openHelpModal, HelpButton };
